import { useState } from 'react';
import styled from 'styled-components';
import useApi from '../../hooks/useApi';
import { useStore, useMessage } from '../pageView/Page';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useTranslation from '../../hooks/useTranslation';
import { resolveVisibility } from "../../../../common/model/condition/evaluate"
import invalidateListQueries from '../../utils/invalidateListQueries';
import generateEndpoint from '../../utils/generateEndpoint';

const ButtonRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-top: 1rem;
`

const ActionButton = styled.button`
    padding: 0.45rem 1.1rem;
    border-radius: 0.375rem;
    border: none;
    font-size: var(--font-S);
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
    background: ${({ $variant, theme }) => {
        if ($variant === 'primary') return theme.btn?.primary || '#0d6efd';
        if ($variant === 'warning') return '#fd7e14';
        if ($variant === 'success') return '#198754';
        return theme.btn?.secondary || '#6c757d';
    }};
    color: #fff;
`

const ConfirmOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
`

const ConfirmBox = styled.div`
    background: ${({ theme }) => theme.form?.background || '#fff'};
    border-radius: 0.5rem;
    padding: 1.5rem;
    min-width: 320px;
    max-width: 480px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
`

const ConfirmTitle = styled.h5`
    margin: 0 0 0.5rem;
    font-size: var(--font-M);
`

const ConfirmBody = styled.p`
    font-size: var(--font-S);
    color: ${({ theme }) => theme.font?.label};
    margin: 0 0 1rem;
`

const ConfirmButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
`

const DisplayButtons = ({ config, cardConfig }) => {
    const [state] = useStore((s) => s);
    const [, setMessage] = useMessage((s) => s);
    const { id } = useParams();
    const { postMe } = useApi();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [confirm, setConfirm] = useState(null);
    const [loading, setLoading] = useState(false);

    const currentState = state?.state;
    const entity = cardConfig?.path;
    const endpoint = generateEndpoint(entity, id);

    const dispatch = async (action, payload = {}) => {
        setLoading(true);
        try {
            const response = await postMe(`${endpoint}/${state.id}/${action}`, { ...state, ...payload });
            if (response?.status === 'success' || response?.success) {
                await queryClient.invalidateQueries({ queryKey: [entity, state.id] });
                await queryClient.invalidateQueries({ queryKey: [endpoint, state.id] });
                await invalidateListQueries(entity, queryClient);
                setMessage({ show: true, title: t('success.updated'), message: '', type: 'success' });
            } else {
                setMessage({ show: true, title: response?.title || '', message: response?.message || '', type: 'warning' });
            }
        } catch (err) {
            setMessage({ show: true, title: '', message: 'Operation failed', type: 'error' });
        } finally {
            setLoading(false);
            setConfirm(null);
        }
    };

    const handleAction = (key, btnConfig) => {
        if (btnConfig.confirm) {
            setConfirm({ key, btnConfig });
        } else {
            dispatch(btnConfig.action, {});
        }
    };

    const handleConfirmAccept = () => {
        if (!confirm) return;
        dispatch(confirm.btnConfig.action, {});
    };

    if (!config || !currentState) return null;

    const visibleButtons = Object.entries(config).filter(
        // A button with no condition stays hidden, as it always has — these are
        // state machines, and a stateless button is a config mistake.
        ([, btnCfg]) => btnCfg.condition
            && resolveVisibility(btnCfg, { values: {}, context: { state: currentState } }) === 'show'
    );

    return (
        <>
            <ButtonRow>
                {visibleButtons.map(([key, btnCfg]) => (
                    <ActionButton
                        key={key}
                        $variant={btnCfg.variant}
                        disabled={loading}
                        onClick={() => handleAction(key, btnCfg)}
                        type="button"
                    >
                        {t(btnCfg.label)}
                    </ActionButton>
                ))}
            </ButtonRow>

            {confirm && (
                <ConfirmOverlay onClick={() => setConfirm(null)}>
                    <ConfirmBox onClick={e => e.stopPropagation()}>
                        <ConfirmTitle>{t(`workExecution.modal.${confirm.key}.title`) || confirm.key}</ConfirmTitle>
                        <ConfirmBody>{t(`workExecution.modal.${confirm.key}.body`)}</ConfirmBody>
                        <ConfirmButtons>
                            <ActionButton $variant="secondary" onClick={() => setConfirm(null)} type="button">
                                {t('all.confirm.cancel')}
                            </ActionButton>
                            <ActionButton $variant={confirm.btnConfig.variant || 'primary'} onClick={handleConfirmAccept} type="button">
                                {t(`workExecution.modal.${confirm.key}.accept`)}
                            </ActionButton>
                        </ConfirmButtons>
                    </ConfirmBox>
                </ConfirmOverlay>
            )}
        </>
    );
};

export default DisplayButtons;
